import { chatLink, formatPhone } from "@rhema/client";
import { Sheet } from "@rhema/ui";
import { useEffect, useId, useState } from "react";
import { useMe, useSession } from "../session/session";
import { ShareTargets } from "./ShareTargets";

/** The address links point at: the Rhema server, which is the page's own origin on the web. */
export function useAppOrigin(): string {
  const { platform } = useSession();
  return platform.serverOrigin() || window.location.origin;
}

/** Your own chat link, which opens a chat with you, or null before your number is known. */
export function useMyChatLink(): string | null {
  const me = useMe();
  const origin = useAppOrigin();
  return me.phone ? chatLink(origin, me.phone) : null;
}

export function inviteMessage(link: string | null, origin: string): string {
  return link
    ? `I use Rhema for messages and calls. Message me here: ${link}`
    : `I use Rhema for messages and calls. You can get it here: ${origin}`;
}

/** Invites someone who is not on Rhema yet, with a message they can change before sharing. */
export function InviteSheet({
  open,
  onClose,
  name,
  phone,
}: {
  open: boolean;
  onClose: () => void;
  /** Who is being invited, when they come from the address book. */
  name?: string;
  phone?: string;
}) {
  const origin = useAppOrigin();
  const link = useMyChatLink();
  const fieldId = useId();
  const [text, setText] = useState(() => inviteMessage(link, origin));
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setText(inviteMessage(link, origin));
      setNote(null);
    }
  }, [open, link, origin]);

  const who = name || (phone ? formatPhone(phone) : "");
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Invite to Rhema"
      subtitle={who ? `${who} is not on Rhema yet` : undefined}
      className="px-5 pb-7"
    >
      <label htmlFor={fieldId} className="mt-4 block text-[13px] font-semibold">
        Message
      </label>
      <textarea
        id={fieldId}
        rows={4}
        maxLength={500}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="mt-1.5 w-full resize-none rounded-field border border-line bg-surface px-4 py-3 font-sans text-[15px] text-ink focus:border-blue"
      />
      <div className="mt-4">
        <ShareTargets
          text={text.trim()}
          {...(link ? { url: link } : {})}
          subject="Join me on Rhema"
          {...(phone ? { phone } : {})}
          onResult={(r) => {
            if (r.note) setNote(r.note);
            if (r.done && !r.note) onClose();
          }}
        />
      </div>
      {note && (
        <p role="status" className="m-0 mt-3 text-[13px] text-ink-secondary">
          {note}
        </p>
      )}
    </Sheet>
  );
}
